/**
 * Image Preloader Component
 * 
 * Dynamically adds <link rel="preload"> tags for critical images
 * to improve LCP (Largest Contentful Paint) performance.
 */

import { useEffect } from "react";

interface ImagePreloaderProps {
  /**
   * Image URL to preload
   */
  imageUrl?: string | null;
  /**
   * Whether this is a critical above-the-fold image (default: true)
   */
  critical?: boolean;
}

export const ImagePreloader: React.FC<ImagePreloaderProps> = ({
  imageUrl,
  critical = true,
}) => {
  useEffect(() => {
    if (!imageUrl || typeof window === "undefined") {
      return;
    }

    // Only preload if it's a critical image and not already preloaded
    if (!critical) {
      return;
    }

    // Check if link already exists
    const existingLink = document.querySelector(
      `link[rel="preload"][as="image"][href="${imageUrl}"]`
    );

    if (existingLink) {
      return; // Already preloaded
    }

    // Create preload link for critical images
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = imageUrl;
    
    // Add fetchpriority for critical images
    if (critical) {
      link.setAttribute("fetchpriority", "high");
    }

    document.head.appendChild(link);

    // Cleanup on unmount
    return () => {
      const linkToRemove = document.querySelector(
        `link[rel="preload"][as="image"][href="${imageUrl}"]`
      );
      if (linkToRemove) {
        linkToRemove.remove();
      }
    };
  }, [imageUrl, critical]);

  return null;
};
